import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../../apiClient';
import './CreateEditPlanModal.css';

function CreateEditPlanModal({ plan, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_annual: '',
    trial_days: 0,
    features: {},
    limits: {},
    is_active: true,
    is_visible: true
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Common features for toggle
  const commonFeatures = useMemo(() => [
    { key: 'basic_analytics', label: 'Basic Analytics' },
    { key: 'advanced_analytics', label: 'Advanced Analytics' },
    { key: 'email_support', label: 'Email Support' },
    { key: 'priority_support', label: 'Priority Support' },
    { key: 'api_access', label: 'API Access' },
    { key: 'white_label', label: 'White Label' },
    { key: 'custom_integrations', label: 'Custom Integrations' },
    { key: 'ai_features', label: 'AI Features' },
    { key: 'dedicated_account_manager', label: 'Dedicated Account Manager' },
    { key: 'sla_guarantee', label: 'SLA Guarantee' }
  ], []);

  // Common limits
  const commonLimits = useMemo(() => [
    { key: 'users', label: 'Max Users', default: 10 },
    { key: 'projects', label: 'Max Projects', default: 5 },
    { key: 'stakeholders', label: 'Max Stakeholders', default: 100 },
    { key: 'assessments_per_month', label: 'Assessments/Month', default: 50 },
    { key: 'storage_gb', label: 'Storage (GB)', default: 10 },
    { key: 'api_calls_per_month', label: 'API Calls/Month', default: 10000 }
  ], []);

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        price_monthly: plan.price_monthly || '',
        price_annual: plan.price_annual || '',
        trial_days: plan.trial_days || 0,
        features: plan.features || {},
        limits: plan.limits || {},
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        is_visible: plan.is_visible !== undefined ? plan.is_visible : true
      });
    } else {
      // Initialize default features and limits for new plan
      const defaultFeatures = {};
      commonFeatures.forEach(f => {
        defaultFeatures[f.key] = false;
      });
      
      const defaultLimits = {};
      commonLimits.forEach(l => {
        defaultLimits[l.key] = l.default;
      });
      
      setFormData(prev => ({
        ...prev,
        features: defaultFeatures,
        limits: defaultLimits
      }));
    }
  }, [plan, commonFeatures, commonLimits]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFeatureToggle = (featureKey) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureKey]: !prev.features[featureKey]
      }
    }));
  };

  const handleLimitChange = (limitKey, value) => {
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [limitKey]: value === '' ? 0 : parseInt(value, 10)
      }
    }));
  };

  const calculateAnnualPrice = () => {
    if (formData.price_monthly) {
      const monthly = parseFloat(formData.price_monthly);
      const annual = monthly * 12 * 0.9; // 10% discount for annual
      setFormData(prev => ({
        ...prev,
        price_annual: annual.toFixed(2)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price_monthly: parseFloat(formData.price_monthly),
        price_annual: parseFloat(formData.price_annual)
      };

      if (plan) {
        await apiClient.put(`/admin/subscription-plans/${plan.id}`, payload);
      } else {
        await apiClient.post('/admin/subscription-plans', payload);
      }

      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content plan-modal">
        <div className="modal-header">
          <h2>{plan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">Plan Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Professional"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Brief description of the plan"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Pricing</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price_monthly">Monthly Price ($) *</label>
                <input
                  type="number"
                  id="price_monthly"
                  name="price_monthly"
                  value={formData.price_monthly}
                  onChange={handleInputChange}
                  onBlur={calculateAnnualPrice}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="price_annual">Annual Price ($) *</label>
                <input
                  type="number"
                  id="price_annual"
                  name="price_annual"
                  value={formData.price_annual}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="trial_days">Trial Period (days)</label>
                <input
                  type="number"
                  id="trial_days"
                  name="trial_days"
                  value={formData.trial_days}
                  onChange={handleInputChange}
                  min="0"
                  max="90"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Features</h3>
            <div className="features-grid">
              {commonFeatures.map(feature => (
                <label key={feature.key} className="feature-toggle">
                  <input
                    type="checkbox"
                    checked={formData.features[feature.key] || false}
                    onChange={() => handleFeatureToggle(feature.key)}
                  />
                  <span>{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Usage Limits</h3>
            <div className="limits-grid">
              {commonLimits.map(limit => (
                <div key={limit.key} className="limit-input">
                  <label htmlFor={`limit-${limit.key}`}>{limit.label}</label>
                  <input
                    type="number"
                    id={`limit-${limit.key}`}
                    value={formData.limits[limit.key] || 0}
                    onChange={(e) => handleLimitChange(limit.key, e.target.value)}
                    min="-1"
                    placeholder="-1 for unlimited"
                  />
                </div>
              ))}
            </div>
            <p className="help-text">Use -1 for unlimited</p>
          </div>

          <div className="form-section">
            <h3>Visibility Settings</h3>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <span>Active (can be purchased)</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_visible"
                  checked={formData.is_visible}
                  onChange={handleInputChange}
                />
                <span>Visible to customers</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (plan ? 'Update Plan' : 'Create Plan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEditPlanModal;